/**
 * Collapsible role-aware sidebar. Active route highlighted; collapses to icon rail.
 * Items belonging to a not-yet-built phase render as disabled with a small phase tag.
 */
import { NavLink } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { NAV_SECTIONS } from '../../config/nav.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import styles from './Sidebar.module.css';

// Phase currently shipped — items above this are interactive; later ones are previews.
const CURRENT_PHASE = 7;

export function Sidebar({ collapsed, onToggle, mobileOpen = false }) {
  const { hasRole } = useAuth();
  // In the mobile drawer we always show full labels regardless of the desktop collapse.
  const showLabels = mobileOpen || !collapsed;
  return (
    <aside
      className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''} ${mobileOpen ? styles.mobileOpen : ''}`}
    >
      <div className={styles.brand}>
        <span className={styles.mark} aria-hidden="true">
          <span className={styles.markRing} />
          <span className={styles.markDot} />
        </span>
        {showLabels && (
          <span className={styles.word}>
            USMEHAR <span className={styles.wordAccent}>CARE</span>
          </span>
        )}
      </div>

      <nav className={styles.nav} aria-label="Main">
        {NAV_SECTIONS.map((section) => {
          const visible = section.items.filter(
            (item) => !item.roles?.length || hasRole(item.roles),
          );
          if (visible.length === 0) return null;
          return (
          <div key={section.title} className={styles.section}>
            {showLabels && <p className={styles.sectionTitle}>{section.title}</p>}
            {visible.map((item) => {
              const upcoming = item.phase && item.phase > CURRENT_PHASE;
              const Icon = item.icon;
              if (upcoming) {
                return (
                  <span
                    key={item.to}
                    className={`${styles.link} ${styles.upcoming}`}
                    title={`Ships in Phase ${item.phase}`}
                  >
                    <Icon size={19} aria-hidden="true" />
                    {showLabels && (
                      <>
                        <span className={styles.label}>{item.label}</span>
                        <span className={styles.phaseTag}>P{item.phase}</span>
                      </>
                    )}
                  </span>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `${styles.link} ${isActive ? styles.active : ''}`
                  }
                  title={!showLabels ? item.label : undefined}
                >
                  <Icon size={19} aria-hidden="true" />
                  {showLabels && <span className={styles.label}>{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>

      <button className={styles.collapseBtn} onClick={onToggle} aria-label="Toggle sidebar">
        {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        {showLabels && <span>Collapse</span>}
      </button>
    </aside>
  );
}

export default Sidebar;
